<div class="registry-page">

  <header class="page-header">
    <div>
      <h2 class="page-title">Agent Studio</h2>
      <p class="page-subtitle">{{ isSystemUser ? 'Create master agents once, then publish independent copies to customer accounts.' : 'Manage the agents assigned to your account.' }}</p>
    </div>
  </header>

  @if (noDomains) {
    <p class="info-banner">No active domains yet. Add one in AI Applications to publish guided and code-based agents.</p>
  }

  @if (isSystemUser) {
    <section class="create-panel" aria-labelledby="agent-create-heading">
      <h3 class="create-panel__title" id="agent-create-heading">Create an agent</h3>
      <div class="creation-actions">
        @for (option of studioOptions; track option.title) {
          <button class="create-card" type="button" [disabled]="optionDisabled(option)" (click)="onStudioOptionClick(option)">
            <span class="card-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath(option.icon)"></path></svg></span>
            <span class="card-copy">
              <strong>{{ option.title }}</strong>
              <small>{{ option.description }}</small>
            </span>
            <svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('arrow')"></path></svg>
          </button>
        }
      </div>
    </section>
  } @else {
    <section class="account-context ar-card">
      <span class="account-context__mark" aria-hidden="true">A</span>
      <div>
        <span class="eyebrow">Account workspace</span>
        <h3>{{ currentAccountName || 'Your assigned agents' }}</h3>
        <p class="muted">These are independent copies. Changes here do not update the master agent or other accounts.</p>
      </div>
    </section>
  }

  @if (activeTab === 'registry' || activeTab === 'accounts') {
    <section class="registry-panel ar-card">
      <nav class="section-tabs" role="tablist" aria-label="Agent registry sections">
        @if (isSystemUser) {
          <button type="button" role="tab" [attr.aria-selected]="activeTab === 'registry'" [class.active]="activeTab === 'registry'" (click)="setTab('registry')">
            Master agents
            <span class="section-tabs__hint">Reusable definitions</span>
          </button>
          <button type="button" role="tab" [attr.aria-selected]="activeTab === 'accounts'" [class.active]="activeTab === 'accounts'" (click)="setTab('accounts')">
            Account agents
            <span class="section-tabs__hint">Independent copies</span>
          </button>
        } @else {
          <button type="button" role="tab" class="active" aria-selected="true">Account agents</button>
        }
      </nav>

      <div class="registry-panel__body">
        @if (activeTab === 'accounts') {
          <p class="copy-note">Each account assignment is a snapshot. Updating or deleting a master agent does not change copies that have already been assigned.</p>
        }
        @if (registryError) {
          <p class="error-banner inset">{{ registryError }}</p>
        }

        <div class="toolbar">
          <div class="field">
            <span id="agent-type-label">Type</span>
            <div class="segmented" role="tablist" aria-labelledby="agent-type-label">
              @for (option of agentTypeOptions; track option.id) {
                <button class="segmented__option" type="button" role="tab" [title]="option.description"
                        [class.active]="selectedType === option.id" [attr.aria-selected]="selectedType === option.id"
                        (click)="setAgentType(option.id)">{{ option.label }}</button>
              }
            </div>
          </div>
          <label class="field grow">
            <span>Search</span>
            <input type="search" placeholder="Agent ID, name, domain, version…" [(ngModel)]="searchTerm" (ngModelChange)="onSearchChange()">
          </label>
          <div class="field">
            <span>Domain</span>
            <kx-multiselect-dropdown [questions]="domainFilterQuestion" [settings]="selectSettings" [data]="domainOptions"
              [placeholder]="'All domains'" [(ngModel)]="selectedDomain"
              (onSelect)="$event?.isManual && onFilterChange()" (onDeSelect)="onFilterChange()"></kx-multiselect-dropdown>
          </div>
          <div class="field field--narrow">
            <span>Status</span>
            <kx-multiselect-dropdown [questions]="statusFilterQuestion" [settings]="selectSettings" [data]="statusOptions"
              [placeholder]="'All'" [(ngModel)]="selectedStatus"
              (onSelect)="$event?.isManual && onFilterChange()" (onDeSelect)="onFilterChange()"></kx-multiselect-dropdown>
          </div>
          @if (isSystemUser) {
            <div class="field" [style.display]="activeTab === 'accounts' ? null : 'none'">
              <span>Account</span>
              <kx-multiselect-dropdown [questions]="accountFilterQuestion" [settings]="accountSettings" [data]="accountSource"
                [placeholder]="'All accounts'" [(ngModel)]="selectedAccountId"
                (onSelect)="$event?.isManual && onFilterChange()" (onDeSelect)="onFilterChange()"></kx-multiselect-dropdown>
            </div>
          }
        </div>

        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Agent</th><th>Application</th><th>Version</th><th>Type</th>
                @if (activeTab === 'accounts' && isSystemUser) {
                  <th>Account</th>
                }
                <th>Status</th><th>Created</th><th><span class="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              @if (gridLoading) {
                <tr>
                  <td [attr.colspan]="activeTab === 'accounts' && isSystemUser ? 8 : 7" class="empty-state">
                    <span class="grid-loader" role="status" aria-live="polite">
                      <span class="grid-loader__spinner" aria-hidden="true"></span>
                      {{ accountContextLoading ? 'Loading workspace…' : 'Loading agents…' }}
                    </span>
                  </td>
                </tr>
              } @else {
                @for (agent of pageAgents; track agent.id) {
                  <tr>
                    <td class="agent-cell">
                      <span class="agent-cell__name">{{ agent.name }}</span>
                      <code class="agent-cell__id">{{ agent.agentId || '—' }}</code>
                    </td>
                    <td>
                      @if (agent.domain) {
                        {{ domainName(agent.domain) }}
                      } @else {
                        <span class="muted">Choose application</span>
                      }
                    </td>
                    <td><span class="version">{{ agent.version || '—' }}</span></td>
                    <td><span class="chip" [class.chip-builder]="agent.authoringPath !== 'code'" [title]="typeLabel(agent)">{{ typeLabel(agent) }}</span></td>
                    @if (activeTab === 'accounts' && isSystemUser) {
                      <td><span class="scope-badge scope-badge--copy" [title]="accountName(agent)">{{ accountName(agent) }}</span></td>
                    }
                    <td>
                      <span class="status" [class.state-active]="agent.status === 'active' || agent.status === 'published'" [class.state-draft]="agent.status === 'draft'" [class.state-inactive]="agent.status !== 'active' && agent.status !== 'published' && agent.status !== 'draft'">{{ statusLabel(agent.status) }}</span>
                    </td>
                    <td class="date-cell">{{ formatDate(agent.created) }}</td>
                    <td class="row-actions">
                      @if (activeTab === 'registry' && isSystemUser) {
                        <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="openEditAgentModal(agent)">{{ isPublishedGuided(agent) || isPublishedWorkflow(agent) ? 'New version' : agent.draftKind === 'guided' ? 'Edit setup' : agent.draftKind === 'workflow' ? 'Edit workflow' : 'Edit' }}</button>
                        @if (agent.draftKind === 'workflow' && !isPublishedWorkflow(agent) && workflowForAgent(agent); as workflow) {
                          <button class="row-btn" type="button" (click)="editDraftDetails(workflow)">Details</button>
                        }
                        @if (canPublishWorkflow(agent)) {
                          <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="publishWorkflow(agent)">Publish</button>
                        }
                        @if (canAssign(agent)) {
                          <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="openAssignAgentModal(agent)">Assign</button>
                        }
                      } @else if (activeTab === 'accounts' && canManageAgent(agent)) {
                        <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="openEditAgentModal(agent)">Edit copy</button>
                      }
                      @if (canRun(agent)) {
                        <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="openRunAgentModal(agent)">Run</button>
                      }
                      @if (canTestDraft(agent)) {
                        <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="openRunAgentModal(agent)">Test</button>
                      }
                      @if (canManageAgent(agent)) {
                        <!-- A workflow is published with its own Publish; a published guided master has no status of its own (its copies do). -->
                        @if (canPublishOrToggle(agent)) {
                          <button class="row-btn" type="button" [disabled]="busyKey === agent.id" (click)="toggleStatus(agent)">{{ agent.status === 'active' ? 'Deactivate' : (agent.draftKind === 'guided' && !agent.accountId) ? 'Continue to publish' : 'Activate' }}</button>
                        }
                        <button class="row-btn row-btn--danger" type="button" [disabled]="busyKey === agent.id" (click)="deleteAgent(agent)">Delete</button>
                      }
                    </td>
                  </tr>
                }
                @if (pageAgents.length === 0) {
                  <tr>
                    <td [attr.colspan]="activeTab === 'accounts' && isSystemUser ? 8 : 7" class="empty-state">
                      <strong class="empty-state__title">{{ activeTab === 'accounts' ? 'No account agents' : 'No master agents' }}</strong>
                      {{ activeTab === 'accounts' ? 'No agents have been assigned to this account yet.' : 'No master agents match the filters. Create an agent above to get started.' }}
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        @if (!gridLoading && totalCount > pageSize) {
          <div class="pager">
            <span class="muted">Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalCount }}</span>
            <div class="pager__pages">
              <button type="button" class="btn-secondary" [disabled]="currentPage === 1" (click)="goToPage(currentPage - 1)">Prev</button>
              @for (page of pageNumbers; track page) {
                <button type="button" class="pager__page" [class.active]="page === currentPage" (click)="goToPage(page)">{{ page }}</button>
              }
              <button type="button" class="btn-secondary" [disabled]="currentPage === totalPages" (click)="goToPage(currentPage + 1)">Next</button>
            </div>
          </div>
        }
      </div>
    </section>
  }

  @if (activeModal !== 'none') {
    <div class="agent-registry-modal__backdrop" (click)="closeModal()"></div>
    <div class="agent-registry-modal">
      <div class="agent-registry-modal__content" [class.agent-registry-modal__content--wide]="activeModal === 'newCodeAgent' || activeModal === 'editAgent' || activeModal === 'runAgent' || activeModal === 'assignAgent'">
        <button type="button" class="agent-registry-modal__close" aria-label="Close" title="Close" (click)="closeModal()" [disabled]="saving">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path [attr.d]="iconPath('close')"></path></svg>
        </button>

        @if (activeModal === 'confirm' && pendingConfirm) {
          <h4 class="agent-registry-modal__title">{{ pendingConfirm.title }}</h4>
          <p class="agent-registry-modal__copy">{{ pendingConfirm.message }}</p>
        }

        @if (activeModal === 'editAgent') {
          <span class="agent-registry-modal__badge">{{ modalAgent?.accountId ? 'Account copy' : 'Master agent' }}</span>
          <h4 class="agent-registry-modal__title">{{ modalAgent?.accountId ? 'Edit account copy' : modalAgent?.status === 'active' ? 'Create a draft revision' : 'Edit code agent draft' }}</h4>
          <p class="agent-registry-modal__copy">
            @if (modalAgent?.accountId) {
              Changes apply only to this account copy. The master agent and other copies remain unchanged.
            } @else if (modalAgent?.status === 'active') {
              Saving creates a new draft version. The active version keeps running until you activate the revision.
            } @else {
              Update the full code-agent definition before activating it.
            }
          </p>
          @if (modalAgent?.authoringPath === 'code') {
            <div class="modal-grid">
              <div class="mb-3">
                <label class="filter-label">Application*</label>
                <kx-multiselect-dropdown [questions]="modalDomainQuestion" [settings]="selectSettings" [data]="domainOptions"
                  [placeholder]="'Choose an application'" [(ngModel)]="formModel.domain" [disabled]="!!modalAgent?.accountId"></kx-multiselect-dropdown>
              </div>
              <div class="mb-3">
                <label class="filter-label">Agent name*</label>
                <input type="text" class="form-control" [(ngModel)]="formModel.agent_name" [disabled]="!!modalAgent?.accountId">
                <div class="form-hint">Agent ID: <code>{{ codeAgentId || formModel.agent_id || '—' }}</code></div>
              </div>
              <div class="mb-3">
                <label class="filter-label">Display name*</label>
                <input type="text" class="form-control" [(ngModel)]="formModel.name">
              </div>
              <div class="mb-3">
                <label class="filter-label">Version*</label>
                <input type="text" class="form-control" [(ngModel)]="formModel.version" [disabled]="!!modalAgent?.accountId">
              </div>
              <div class="mb-3">
                <label class="filter-label">Lifecycle*</label>
                <kx-multiselect-dropdown [questions]="modalLifecycleQuestion" [settings]="selectSettings" [data]="lifecycleOptions"
                  [placeholder]="'Choose a lifecycle'" [(ngModel)]="formModel.lifecycle"></kx-multiselect-dropdown>
              </div>
              <div class="mb-3 modal-grid__full">
                <label class="filter-label">Input schema (JSON)</label>
                <textarea class="form-control run-input" rows="5" [(ngModel)]="formModel.input_schema"></textarea>
              </div>
              <div class="mb-3 modal-grid__full">
                <label class="filter-label">Output schema (JSON)</label>
                <textarea class="form-control run-input" rows="5" [(ngModel)]="formModel.output_schema"></textarea>
              </div>
              <div class="mb-3 modal-grid__full">
                <ng-container [ngTemplateOutlet]="codeAgentKnowledge"></ng-container>
              </div>
            </div>
          } @else {
            <div class="mb-3">
              <label class="filter-label">Display name*</label>
              <input type="text" class="form-control" [(ngModel)]="formModel.name">
            </div>
            <div class="mb-3">
              <label class="filter-label">Version*</label>
              <input type="text" class="form-control" [(ngModel)]="formModel.version" [disabled]="!!modalAgent?.accountId">
            </div>
            <div class="mb-3">
              <label class="filter-label">Lifecycle*</label>
              <kx-multiselect-dropdown [questions]="modalLifecycleQuestion" [settings]="selectSettings" [data]="lifecycleOptions"
                [placeholder]="'Choose a lifecycle'" [(ngModel)]="formModel.lifecycle"></kx-multiselect-dropdown>
            </div>
          }
        }

        @if (activeModal === 'assignAgent') {
          <span class="agent-registry-modal__badge">Publish a copy</span>
          <h4 class="agent-registry-modal__title">Assign {{ modalAgent?.agentId }} to an account</h4>
          <p class="agent-registry-modal__copy">This creates an independent snapshot for the selected account. Master-agent changes will not be applied to this copy.</p>
          <div class="mb-3">
            <label class="filter-label">Account*</label>
            <kx-multiselect-dropdown [questions]="assignAccountQuestion" [settings]="accountSettings" [data]="accountSource"
              [placeholder]="'Choose an account'" [ngModel]="formModel.account_id"
              (onSelect)="$event?.isManual && onAssignmentAccountSelect($event)" (onDeSelect)="onAssignmentAccountSelect(null)"></kx-multiselect-dropdown>
          </div>
          @if (formModel.account_id && (!assignmentDomainsLoaded || !assignmentChecked)) {
            <p class="muted">Checking the account's applications and copies…</p>
          } @else if (formModel.account_id && assignmentTaken) {
            <p class="error-banner">This agent version is already assigned to that account.</p>
          } @else if (formModel.account_id && assignmentDomainsLoaded && !assignmentDomainAvailable) {
            <p class="error-banner">This account does not have the <strong>{{ domainName(modalAgent?.domain || '') }}</strong> application mapped. Map the application to the account before assigning the agent.</p>
          } @else if (assignmentDomainAvailable) {
            <div class="assignment-summary">
              <span class="eyebrow">Application</span>
              <strong>{{ domainName(modalAgent?.domain || '') }}</strong>
            </div>
          }
          <div class="mb-3">
            <label class="filter-label">Copy name*</label>
            <input type="text" class="form-control" [(ngModel)]="formModel.name">
            <small class="form-hint">You can rename the copy without changing the master.</small>
          </div>
        }

        @if (activeModal === 'newDraft' || activeModal === 'editDraft') {
          @if (activeModal === 'newDraft') {
            <span class="agent-registry-modal__badge">Workflow builder</span>
          }
          <h4 class="agent-registry-modal__title">{{ activeModal === 'newDraft' ? 'New workflow draft' : 'Edit draft details' }}</h4>
          @if (activeModal === 'newDraft') {
            <p class="agent-registry-modal__copy">Associate the workflow with an application, then design it on the canvas. Published versions remain unchanged by later edits.</p>
          }
          <div class="mb-3">
            <label class="filter-label">Application*</label>
            <kx-multiselect-dropdown [questions]="modalDomainQuestion" [settings]="selectSettings" [data]="domainOptions"
              [placeholder]="'Choose an application'" [(ngModel)]="formModel.domain"
              (onSelect)="$event?.isManual && onWorkflowDomainChange($event.id)" (onDeSelect)="onWorkflowDomainChange('')"></kx-multiselect-dropdown>
          </div>
          <div class="mb-3">
            <label class="filter-label">Flow name*</label>
            <input type="text" class="form-control" placeholder="claims-triage" [(ngModel)]="formModel.flow_name" (ngModelChange)="onWorkflowDomainChange(formModel.domain)">
          </div>
          <div class="mb-3">
            <label class="filter-label">Agent ID*</label>
            <input type="text" class="form-control" placeholder="claims-triage" [(ngModel)]="formModel.agent_id">
          </div>
          <div class="mb-3">
            <label class="filter-label">Version*</label>
            <input type="text" class="form-control" [(ngModel)]="formModel.version">
          </div>
          <div class="mb-3">
            <label class="filter-label">Description</label>
            <textarea class="form-control" rows="3" [(ngModel)]="formModel.description"></textarea>
          </div>
        }

        @if (activeModal === 'publishDraft') {
          <span class="agent-registry-modal__badge">Publish</span>
          <h4 class="agent-registry-modal__title">Publish workflow</h4>
          <p class="agent-registry-modal__copy">Choose the domain and version to publish. A published workflow can be assigned to any account, which gets its own copy to edit; to change the master, start its next version.</p>
          <div class="mb-3">
            <label class="filter-label">Domain runtime*</label>
            <kx-multiselect-dropdown [questions]="modalDomainQuestion" [settings]="selectSettings" [data]="domainOptions"
              [placeholder]="'Choose a domain'" [(ngModel)]="formModel.domain"
              (onSelect)="$event?.isManual && onPublishDomainChange($event.id)" (onDeSelect)="onPublishDomainChange('')"></kx-multiselect-dropdown>
          </div>
          <div class="mb-3">
            <label class="filter-label">Agent ID*</label>
            <input type="text" class="form-control" placeholder="billing.visual-summary" [(ngModel)]="formModel.agent_id">
          </div>
          <div class="mb-3">
            <label class="filter-label">Version*</label>
            <input type="text" class="form-control" placeholder="v1" [(ngModel)]="formModel.version">
          </div>
        }

        @if (activeModal === 'newCodeAgent') {
          <span class="agent-registry-modal__badge">Domain-scoped</span>
          <h4 class="agent-registry-modal__title">Create code-based agent draft</h4>
          <p class="agent-registry-modal__copy">Define the agent and its contract for an application. Activate the draft when it is ready to run.</p>
          <div class="modal-grid">
            <div class="mb-3">
              <label class="filter-label">Domain*</label>
              <kx-multiselect-dropdown [questions]="modalDomainQuestion" [settings]="selectSettings" [data]="domainOptions"
                [placeholder]="'Choose a domain'" [(ngModel)]="formModel.domain"></kx-multiselect-dropdown>
            </div>
            <div class="mb-3">
              <label class="filter-label">Agent name*</label>
              <input type="text" class="form-control" placeholder="invoice-summarizer" [(ngModel)]="formModel.agent_name">
              <div class="form-hint">Agent ID: <code>{{ codeAgentId || '—' }}</code></div>
            </div>
            <div class="mb-3">
              <label class="filter-label">Display name*</label>
              <input type="text" class="form-control" placeholder="Invoice Summarizer" [(ngModel)]="formModel.name">
            </div>
            <div class="mb-3">
              <label class="filter-label">Version*</label>
              <input type="text" class="form-control" [(ngModel)]="formModel.version">
            </div>
            <div class="mb-3 modal-grid__full">
              <label class="filter-label">Lifecycle*</label>
              <kx-multiselect-dropdown [questions]="modalLifecycleQuestion" [settings]="selectSettings" [data]="lifecycleOptions"
                [placeholder]="'Choose a lifecycle'" [(ngModel)]="formModel.lifecycle"></kx-multiselect-dropdown>
            </div>
            <div class="mb-3">
              <label class="filter-label">Input schema (JSON, optional)</label>
              <textarea class="form-control run-input" rows="5" placeholder="{}" [(ngModel)]="formModel.input_schema"></textarea>
            </div>
            <div class="mb-3">
              <label class="filter-label">Output schema (JSON, optional)</label>
              <textarea class="form-control run-input" rows="5" placeholder="{}" [(ngModel)]="formModel.output_schema"></textarea>
            </div>
            <div class="mb-3 modal-grid__full">
              <ng-container [ngTemplateOutlet]="codeAgentKnowledge"></ng-container>
            </div>
          </div>
          @if (formModel.domain && codeAgentId) {
            <p class="text-muted small">The {{ formModel.domain }} runtime must implement <code>{{ codeAgentId }}</code> at this version.</p>
          }
        }

        @if (activeModal === 'runAgent') {
          @if (runTarget && canTestDraft(runTarget)) {
            <h4 class="agent-registry-modal__title">Test {{ runTarget.agentId || runTarget.name }} <span class="text-muted">draft</span></h4>
            <p class="agent-registry-modal__copy">Runs the master's current setup, unpublished changes included, as your selected account, the same as the Test step in the setup. Nothing is recorded, and account copies keep running their own published version.</p>
          } @else {
            <h4 class="agent-registry-modal__title">Run {{ runTarget?.agentId }} <span class="text-muted">{{ runTarget?.version }}</span></h4>
          }
          <div class="mb-3">
            <label class="filter-label">Input (JSON)</label>
            <textarea class="form-control run-input" rows="6" [(ngModel)]="formModel.input"></textarea>
          </div>
          @if (draftReply) {
            <div class="mb-3">
              <label class="filter-label">Response</label>
              <pre class="run-output">{{ draftReply }}</pre>
            </div>
          }
          @if (runTask) {
            <div class="mb-3">
              <label class="filter-label">Result</label>
              <div class="run-status">
                <span class="status" [class.state-active]="runTask.status === 'succeeded'" [class.state-inactive]="runTask.status !== 'succeeded'">{{ runTask.status }}</span>
                <code class="text-muted">{{ runTask.task_id }}</code>
                @if (runTask.retry_count) {
                  <span class="text-muted small">retried {{ runTask.retry_count }}×</span>
                }
              </div>
              <pre class="run-output">{{ runResultText }}</pre>
            </div>
          }
        }

        @if (modalError) {
          <div class="registry-banner registry-banner--error">{{ modalError }}</div>
        }

        <div class="agent-registry-modal__actions">
          <button type="button" class="btn-secondary" (click)="closeModal()" [disabled]="saving">{{ activeModal === 'runAgent' ? 'Close' : 'Cancel' }}</button>
          <button type="button" class="btn-primary" [class.btn-primary--danger]="activeModal === 'confirm'"
                  (click)="submitModal()" [disabled]="submitDisabled">{{ submitLabel }}</button>
        </div>
      </div>
    </div>
  }

</div>

<ng-template #codeAgentKnowledge>
  <label class="filter-label">Knowledge bases</label>
  <div class="picker-inline">
    <kx-multiselect-dropdown [questions]="knowledgeQuestion" [settings]="accountSettings" [data]="knowledgeSource"
      [placeholder]="'Search knowledge bases'" [ngModel]="knowledgePick" (onSelect)="$event?.isManual && pickKnowledgeBase($event)"
      (onDeSelect)="pickKnowledgeBase(null)"></kx-multiselect-dropdown>
    <button type="button" class="btn-secondary" [disabled]="!knowledgePick" (click)="addKnowledgeBase()">Add</button>
  </div>
  @for (kb of formModel.knowledge_bases || []; track kb.id; let i = $index) {
    <div class="kb-row">
      <div class="kb-row__text">
        <strong>{{ kb.name }}</strong>
        @if (kb.status) {
          <span class="kb-row__status">{{ kb.status }}</span>
        }
        @if (kb.description) {
          <div class="form-hint">{{ kb.description }}</div>
        }
      </div>
      <button type="button" class="row-btn row-btn--danger" (click)="removeKnowledgeBase(i)">Remove</button>
    </div>
  }
  <div class="form-hint">The runtime searches these with <code>platform_sdk.knowledge</code>; each run gets their ids in <code>context.config.knowledge_base_ids</code>. An account copy can use only global knowledge bases and its own account's, built (Completed).</div>
</ng-template>
